// Shared display + meeting-cadence helpers for the PTM/PL profile "practice work" widgets
// (Constraints & Actions by Practice, Upcoming Meetings). One source of truth so the table
// and the runway never drift on labels, dates, or meeting status. Both read the same
// next-meetings payload (ops_summary.next_meetings): per practice next_scheduled, last_meeting,
// days_until_due, lapsed, prioritized_count, items[].

const ACRONYMS = new Set([
  'awv', 'hcc', 'tcm', 'toc', 'cdi', 'npi', 'tin', 'emr', 'ed', 'er',
  'pcp', 'dme', 'p4p', 'aco', 'spv', 'acd', 'adt',
]);

// Acronym-aware titleizer for initiative keys — works for every tenant's keys, not a fixed map.
export function initiativeLabel(key) {
  return (key || '—').split(/[\s_]+/)
    .map(w => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

// Parse a YYYY-MM-DD as local midnight (avoids the UTC-offset off-by-one).
function localDate(d) {
  return d ? new Date(`${d}T00:00:00`) : null;
}

export function fmtDate(d) {
  const dt = localDate(d);
  return dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
}

export function ago(d) {
  const dt = localDate(d);
  if (!dt) return '—';
  const days = Math.round((Date.now() - dt.getTime()) / 864e5);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 45) return `${days} days ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  return `${(days / 365).toFixed(1)} years ago`;
}

// A practice's next-meeting standing, from data we actually know: a real scheduled future
// meeting, else "none scheduled" with how long since we last met. Never a cadence guess.
export function dueLabel(p) {
  if (p.next_scheduled) return `Scheduled ${fmtDate(p.next_scheduled)}`;
  if (p.last_meeting) return `None scheduled (last met ${ago(p.last_meeting)})`;
  return 'No meetings yet';
}

export function dueClass(p) {
  if (p.next_scheduled && p.days_until_due !== null && p.days_until_due <= 7) return 'bg-blue-50 text-blue-700';
  return 'bg-gray-100 text-gray-600';
}

// Runway buckets, left→right. Status colors are reserved and always shipped with a label
// (the column header) — never color alone. `lapsed` = the alert state (no meeting scheduled
// and 120d+ / never met).
export const RUNWAY_BUCKETS = [
  { key: 'lapsed', label: 'Lapsed', hint: 'No meeting scheduled, 120+ days since last', dot: '#DC2626', tint: 'bg-red-50' },
  { key: 'week', label: 'This week', hint: 'Meeting scheduled within 7 days', dot: '#2563EB', tint: 'bg-blue-50' },
  { key: 'soon', label: 'Next 30 days', hint: 'Meeting scheduled in 8–30 days', dot: '#0E8C9E', tint: 'bg-teal-50' },
  { key: 'later', label: 'Later', hint: 'Meeting scheduled beyond 30 days', dot: '#6B7280', tint: 'bg-gray-50' },
  { key: 'none', label: 'No meeting set', hint: 'No meeting scheduled (met within 120 days)', dot: '#9CA3AF', tint: 'bg-gray-50' },
];

export function meetingBucket(p) {
  if (p.next_scheduled) {
    const d = p.days_until_due;
    if (d !== null && d <= 7) return 'week';
    if (d !== null && d <= 30) return 'soon';
    return 'later';
  }
  return p.lapsed ? 'lapsed' : 'none';
}

// Open-work count for a practice (constraints + actions), used to size a runway dot.
export function openCount(p) {
  return (p.items || []).length;
}
